import React, { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Platform } from "react-native"

import { isIos } from "@braintree/browser-detection"
import styled from "styled-components/native"

import { ShorthandDuration } from "@treefort/lib/parse-duration"

import { getTextStyleString } from "../lib/text-style"
import { Button } from "./button"
import Modal from "./modal"
import Row from "./row"
import Text from "./text"
import TextInput from "./text-input"

const FooterRow = styled(Row)`
  margin-top: ${({ theme }) => theme.spacing.large}px;
`

const StyledTextInput = styled(TextInput)`
  ${(props) => getTextStyleString("headingLarge", props.theme)};
  padding: ${({ theme }) => theme.textInput.paddingHorizontal}px;
  flex: 1;
  color: ${(props) => props.theme.colors.text.primary};
  /* Unset the default min-width applied by most browsers */
  min-width: 0;
  max-width: 100px;
  text-align: center;
  /* Hide the focus ring. A big a11y faux pas. The input is giant though, it's
  the only one on the page, the default focus ring looks bad, and RN web makes
  it very hard to change the styles. */
  ${Platform.OS === "web" ? "outline-width: 0;" : ""}
  ${Platform.OS === "web" ? "outline-style: none;" : ""}
`

const Container = styled.View`
  padding: ${({ theme }) => theme.spacing.large}px;
`

export function DurationInputModal({
  label,
  open,
  valueMinutes,
  onChange,
  onClose,
}: {
  label: string
  open: boolean
  valueMinutes?: number
  onChange: (value: ShorthandDuration) => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [minutes, setMinutes] = useState(valueMinutes ?? 0)

  const inputText = useRef<TextInput>(null)

  const submit = () => {
    onChange(`${minutes}m`)
    onClose()
  }

  // Mobile Safari is just awful about auto-focusing and selecting text input.
  const isMobileSafari = Platform.OS === "web" && isIos()

  return (
    <Modal
      title={label}
      open={open}
      onPressCloseButton={onClose}
      onPressOutside={onClose}
      backgroundColor="tertiary"
      portalHost="foreground"
      maxWidth={250}
      keyboardAvoidingViewBehavior="padding"
    >
      {() => (
        <Container>
          <Row alignItems="flex-end" justifyContent="center">
            <StyledTextInput
              ref={inputText}
              value={minutes.toString()}
              autoFocus={!isMobileSafari}
              selectTextOnFocus={!isMobileSafari}
              onFocus={(event) => {
                if (isMobileSafari) {
                  const input = event.target as unknown as HTMLInputElement
                  setTimeout(() =>
                    input.setSelectionRange(
                      input.value.length,
                      input.value.length,
                    ),
                  )
                }

                if (Platform.OS === "web") {
                  // On the web, selectTextOnFocus={true} works to select the contents on focus,
                  // and for some reason the web TextInput ref doesn't have a setSelection method.
                  return
                }

                // On native, selectTextOnFocus doesn't select the text, so do it ourselves.
                inputText.current?.setSelection(0, minutes.toString().length)
              }}
              onChangeText={(value) => {
                const mins = parseInt(value)
                setMinutes(isNaN(mins) ? 0 : mins)
              }}
              maxLength={3}
              keyboardType="numeric"
              onSubmitEditing={submit}
            />
          </Row>
          <Row justifyContent="center">
            <Text textStyle="body">{t("minutes")}</Text>
          </Row>
          <FooterRow justifyContent="center">
            <Button
              id="duration-input-modal-save"
              type="primary"
              onPress={submit}
            >
              {t("Save")}
            </Button>
          </FooterRow>
        </Container>
      )}
    </Modal>
  )
}
